import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient, { formatApiError } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import * as PhosphorIcons from '@phosphor-icons/react';

const { Plus, Trash, UserCircle, Storefront, ShieldCheck, Play, ToggleLeft, ToggleRight, PencilSimple } = PhosphorIcons;

const DEFAULT_PERMS = ['dashboard', 'library', 'playlists', 'dayparting', 'calendar', 'announcements', 'analytics', 'legal'];
const PERM_MODULES = [
  { id: 'dashboard', label: 'Dashboard & Remote Control' },
  { id: 'library', label: 'Music Library' },
  { id: 'playlists', label: 'Playlists' },
  { id: 'dayparting', label: 'Dayparting' },
  { id: 'calendar', label: 'Calendar / Occasions' },
  { id: 'announcements', label: 'Announcements' },
  { id: 'analytics', label: 'Analytics' },
  { id: 'legal', label: 'Legal & Licensing' },
];

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editData, setEditData] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'kiosk',
    store_id: '',
    permissions: [...DEFAULT_PERMS]
  });

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, subRes, storesRes] = await Promise.all([
        apiClient.get('/tenant/users'),
        apiClient.get('/subscription/info'),
        apiClient.get('/stores'),
      ]);
      setUsers(usersRes.data);
      setSubscriptionInfo(subRes.data);
      setStores(storesRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.role !== 'kiosk') {
        payload.store_id = null;
      } else if (!payload.store_id) {
        toast.error('Please assign a location for the kiosk user');
        return;
      }
      if (payload.role !== 'manager') {
        delete payload.permissions;
      }
      await apiClient.post('/tenant/users', payload);
      toast.success('User added successfully');
      setOpen(false);
      setFormData({ email: '', password: '', name: '', role: 'kiosk', store_id: '', permissions: ['dashboard', 'library', 'playlists', 'dayparting', 'calendar', 'announcements', 'analytics', 'legal'] });
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Remove this user?')) return;
    try {
      await apiClient.delete(`/tenant/users/${userId}`);
      toast.success('User removed');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await apiClient.patch(`/tenant/users/${userId}`, { is_active: !currentActive });
      toast.success(currentActive ? 'User disabled' : 'User enabled');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleChangeStore = async (userId, storeId) => {
    try {
      await apiClient.patch(`/tenant/users/${userId}`, { store_id: storeId });
      toast.success('Store assignment updated');
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const openEditDialog = (u) => {
    setEditingUser(u);
    setEditData({
      name: u.name || '',
      email: u.email || '',
      password: '',
      role: u.role,
      store_id: u.store_id || '',
      permissions: Array.isArray(u.permissions) && u.permissions.length > 0 ? [...u.permissions] : [...DEFAULT_PERMS],
    });
  };

  const closeEditDialog = () => {
    setEditingUser(null);
    setEditData(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser || !editData) return;
    try {
      const payload = {
        name: editData.name,
        email: editData.email,
      };
      // Password: only send if provided
      if (editData.password && editData.password.trim().length > 0) {
        payload.password = editData.password;
      }
      // Role changes are only allowed for non-admin users
      if (editingUser.role !== 'tenant_admin') {
        payload.role = editData.role;
        if (editData.role === 'kiosk') {
          if (!editData.store_id) {
            toast.error('Please assign a location for the kiosk user');
            return;
          }
          payload.store_id = editData.store_id;
        }
        if (editData.role === 'manager') {
          payload.permissions = editData.permissions;
        }
      }
      await apiClient.patch(`/tenant/users/${editingUser.id}`, payload);
      toast.success('User updated successfully');
      closeEditDialog();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const toggleEditPerm = (modId) => {
    setEditData((prev) => {
      const perms = prev.permissions || [];
      return {
        ...prev,
        permissions: perms.includes(modId) ? perms.filter((p) => p !== modId) : [...perms, modId],
      };
    });
  };

  const getStoreName = (storeId) => {
    const s = stores.find((st) => st.id === storeId);
    return s ? s.name : 'Unassigned';
  };

  if (loading) return <div className="text-[#6b665c] font-mono">Loading...</div>;

  const canAddUsers = users.length < (subscriptionInfo?.limits?.users || 1);
  const isAdmin = user?.role === 'tenant_admin';
  const isManager = user?.role === 'manager';
  // Managers see and manage kiosk users only. Tenant admin sees everyone.
  const visibleUsers = isManager ? users.filter((u) => u.role === 'kiosk') : users;
  const adminCount = users.filter((u) => u.role === 'tenant_admin').length;
  const kioskCount = users.filter((u) => u.role === 'kiosk').length;
  const managerCount = users.filter((u) => u.role === 'manager').length;

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2" data-testid="users-title">Team Members</h1>
          <p className="text-[#6b665c] font-mono text-sm tracking-wider uppercase">
            {users.length} / {subscriptionInfo?.limits?.users || 1} users
            <span className="ml-3 text-[#8a8477]">({adminCount} admin, {managerCount} manager, {kioskCount} kiosk)</span>
          </p>
        </div>
        {(isAdmin || isManager) && (
          <Dialog open={open} onOpenChange={(next) => {
            // Force kiosk role for managers when opening the create dialog
            // so the (hidden) manager/admin options can't be reached via
            // stale state and the backend never receives a non-kiosk role.
            if (next && isManager) setFormData((prev) => ({ ...prev, role: 'kiosk' }));
            setOpen(next);
          }}>
            <DialogTrigger asChild>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white border-0"
                disabled={!canAddUsers}
                data-testid="add-user-button"
              >
                <Plus size={20} className="mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
              <DialogHeader>
                <DialogTitle className="font-mono">Add Team Member</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="add-user-form">
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                    required
                    data-testid="user-email-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                    required
                    minLength={6}
                    data-testid="user-password-input"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Role</Label>
                  <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                      <SelectItem value="kiosk">Kiosk User (location kiosk access only)</SelectItem>
                      {!isManager && <SelectItem value="manager">Manager (music, playlists, scheduling, analytics)</SelectItem>}
                      {!isManager && adminCount < 1 && <SelectItem value="tenant_admin">Admin (full access)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>

                {formData.role === 'manager' && (
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Module Permissions</Label>
                    <div className="bg-[#f5f0e8] border border-[#c5d4c8] rounded-lg p-3 space-y-2" data-testid="manager-permissions">
                      {PERM_MODULES.map(mod => (
                        <label key={mod.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(formData.permissions || []).includes(mod.id)}
                            onChange={() => {
                              setFormData(prev => {
                                const perms = prev.permissions || [];
                                return { ...prev, permissions: perms.includes(mod.id) ? perms.filter(p => p !== mod.id) : [...perms, mod.id] };
                              });
                            }}
                            className="rounded border-[#c5d4c8]"
                            data-testid={`perm-${mod.id}`}
                          />
                          <span className="text-sm text-[#2d2a26]">{mod.label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-[#8a8477] font-mono mt-1">Select which modules this manager can access.</p>
                  </div>
                )}

                {formData.role === 'kiosk' && (
                  <div>
                    <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Assign to Location</Label>
                    <Select value={formData.store_id} onValueChange={(value) => setFormData({ ...formData, store_id: value })}>
                      <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="user-store-select">
                        <SelectValue placeholder="Select a store..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                        {stores.map((store) => (
                          <SelectItem key={store.id} value={store.id}>
                            {store.name} — {store.location}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-[#8a8477] font-mono mt-1">This user will only access the kiosk player for this location. No other location or module will be visible.</p>
                  </div>
                )}

                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0" data-testid="submit-user-button">
                  Add User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {!canAddUsers && (isAdmin || isManager) && (
        <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-md p-4">
          <p className="text-yellow-400 text-sm font-mono">
            User limit reached. <a href="/dashboard/subscription" className="underline font-bold">Upgrade plan</a> to add more.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleUsers.map((u) => (
          <div
            key={u.id}
            className={`bg-white border rounded-md p-5 transition-all duration-200 hover:-translate-y-1 ${
              u.is_active === false ? 'border-[#c5d4c8]/50 opacity-60' : 'border-[#c5d4c8] hover:border-[#c5d4c8]'
            }`}
            data-testid={`user-card-${u.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  u.role === 'tenant_admin' ? 'bg-[#fce8eb]/20 border border-red-800/30' :
                  u.role === 'manager' ? 'bg-blue-50 border border-blue-200' : 'bg-[#e4ebe5]'
                }`}>
                  {u.role === 'tenant_admin' ? (
                    <ShieldCheck size={20} className="text-red-400" />
                  ) : u.role === 'manager' ? (
                    <ShieldCheck size={20} className="text-blue-500" />
                  ) : (
                    <Play size={20} className="text-[#7c9082]" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-sm">{u.name}</h3>
                  <p className="text-xs text-[#8a8477] font-mono">{u.email}</p>
                </div>
              </div>
            </div>

            {/* Role badge + store */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                  u.role === 'tenant_admin' ? 'bg-[#fce8eb]/20 text-red-400 border border-red-500/30' :
                  u.role === 'manager' ? 'bg-blue-50 text-blue-600 border border-blue-200' :
                  'bg-[#e4ebe5] text-[#7c9082] border border-[#c5d4c8]'
                }`}>
                  {u.role === 'tenant_admin' ? 'ADMIN' : u.role === 'manager' ? 'MANAGER' : 'KIOSK USER'}
                </span>
                {u.is_active === false && (
                  <span className="px-2 py-0.5 rounded text-xs font-mono bg-[#e4ebe5] text-[#8a8477]">DISABLED</span>
                )}
              </div>

              {u.role === 'kiosk' && (
                <div className="flex items-center gap-2 text-xs">
                  <Storefront size={14} className="text-[#8a8477]" />
                  {isAdmin ? (
                    <Select value={u.store_id || ''} onValueChange={(val) => handleChangeStore(u.id, val)}>
                      <SelectTrigger className="h-7 bg-[#f5f0e8] border-[#c5d4c8] text-xs w-48" data-testid={`reassign-store-${u.id}`}>
                        <SelectValue placeholder="Assign store..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                        {stores.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-[#6b665c]">{getStoreName(u.store_id)}</span>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            {u.role !== 'tenant_admin' && isAdmin && (
              <div className="flex items-center gap-2 pt-2 border-t border-[#c5d4c8]">
                <Button
                  onClick={() => openEditDialog(u)}
                  className="flex-1 bg-[#e4ebe5] hover:bg-[#d2ddd5] text-[#3f5246] border border-[#c5d4c8] h-8 text-xs font-mono"
                  data-testid={`edit-user-${u.id}`}
                >
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
                <Button
                  onClick={() => handleToggleActive(u.id, u.is_active !== false)}
                  className="flex-1 bg-[#f5f0e8] hover:bg-[#e4ebe5] text-[#5a6e5f] border border-[#c5d4c8] h-8 text-xs font-mono"
                  data-testid={`toggle-user-${u.id}`}
                >
                  {u.is_active === false ? (
                    <><ToggleLeft size={14} className="mr-1" /> Enable</>
                  ) : (
                    <><ToggleRight size={14} className="mr-1" /> Disable</>
                  )}
                </Button>
                <Button
                  onClick={() => handleDelete(u.id)}
                  className="bg-[#fce8eb]/20 hover:bg-[#fce8eb]/40 text-red-400 border border-red-800/30 h-8 px-3"
                  data-testid={`delete-user-${u.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            )}
            {u.role === 'tenant_admin' && isAdmin && u.id === user?.id && (
              <div className="flex items-center gap-2 pt-2 border-t border-[#c5d4c8]">
                <Button
                  onClick={() => openEditDialog(u)}
                  className="flex-1 bg-[#e4ebe5] hover:bg-[#d2ddd5] text-[#3f5246] border border-[#c5d4c8] h-8 text-xs font-mono"
                  data-testid={`edit-user-${u.id}`}
                >
                  <PencilSimple size={14} className="mr-1" /> Edit Profile
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      {visibleUsers.length === 0 && (
        <div className="text-center py-12 bg-white border border-[#c5d4c8] rounded-md">
          <p className="text-[#8a8477] font-mono">No team members yet.</p>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(o) => { if (!o) closeEditDialog(); }}>
        <DialogContent className="bg-white border-[#c5d4c8] text-[#2d2a26] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono">Edit {editingUser?.role === 'tenant_admin' ? 'Profile' : 'Team Member'}</DialogTitle>
          </DialogHeader>
          {editData && (
            <form onSubmit={handleEditSubmit} className="space-y-4" data-testid="edit-user-form">
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Name</Label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                  required
                  data-testid="edit-user-name-input"
                />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Email</Label>
                <Input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                  required
                  data-testid="edit-user-email-input"
                />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">New Password (leave blank to keep current)</Label>
                <Input
                  type="password"
                  value={editData.password}
                  onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                  className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]"
                  minLength={6}
                  placeholder="••••••"
                  data-testid="edit-user-password-input"
                />
              </div>

              {editingUser?.role !== 'tenant_admin' && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Role</Label>
                  <Select value={editData.role} onValueChange={(value) => setEditData({ ...editData, role: value })}>
                    <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="edit-user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                      <SelectItem value="kiosk">Kiosk User (location kiosk access only)</SelectItem>
                      <SelectItem value="manager">Manager (music, playlists, scheduling, analytics)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {editingUser?.role !== 'tenant_admin' && editData.role === 'manager' && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f] mb-2 block">Module Permissions</Label>
                  <div className="bg-[#f5f0e8] border border-[#c5d4c8] rounded-lg p-3 space-y-2" data-testid="edit-manager-permissions">
                    {PERM_MODULES.map(mod => (
                      <label key={mod.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(editData.permissions || []).includes(mod.id)}
                          onChange={() => toggleEditPerm(mod.id)}
                          className="rounded border-[#c5d4c8]"
                          data-testid={`edit-perm-${mod.id}`}
                        />
                        <span className="text-sm text-[#2d2a26]">{mod.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {editingUser?.role !== 'tenant_admin' && editData.role === 'kiosk' && (
                <div>
                  <Label className="font-mono text-xs uppercase tracking-wider text-[#5a6e5f]">Assign to Location</Label>
                  <Select value={editData.store_id} onValueChange={(value) => setEditData({ ...editData, store_id: value })}>
                    <SelectTrigger className="mt-2 bg-[#f5f0e8] border-[#c5d4c8] text-[#2d2a26]" data-testid="edit-user-store-select">
                      <SelectValue placeholder="Select a store..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#c5d4c8] text-[#2d2a26]">
                      {stores.map((store) => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name} — {store.location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={closeEditDialog}
                  className="flex-1 bg-[#f5f0e8] hover:bg-[#e4ebe5] text-[#5a6e5f] border border-[#c5d4c8]"
                  data-testid="cancel-edit-user-button"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white border-0"
                  data-testid="save-edit-user-button"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
